import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { WeScreen } from '@/components/WeScreen/WeScreen'
import {
  EUserPermission,
  EUserRole,
} from '@/hooks/usePermissions/permissions.type'
import { usePermissions } from '@/hooks/usePermissions/usePermissions'
import { useRedirectBasedOnPermissions } from '@/hooks/useRedirectBasedOnPermissions'
import { clearNewSearch } from '@/store/api/NewSearch/NewSearch.slice'
import { useUserInfoQuery } from '@/store/api/Users/Users.query'
import { setUserInfo } from '@/store/api/Users/Users.slice'
import {
  selectIsPending,
  selectItsDefaultState,
  selectPlan,
} from '@/store/api/Users/Users.selector'

import { ImpersonationCard } from './components/ImpersonationCard'
import { PaymentCard } from './components/PaymentCard'
import { ReportCard } from './components/ReportCard'
import { TopupCreditsCard } from './components/TopupCreditsCard'
import { UserCard } from './components/UserCard'

export const Home = () => {
  const { hasPermission } = usePermissions()
  hasPermission(EUserPermission.HANDLE_REPORTS, EUserRole.READER)

  const dispatch = useDispatch()

  const {
    data: userData,
    isError,
    isLoading,
    credits,
    hasActiveSubscription,
    isPending,
  } = useUserInfoQuery(undefined, {
    selectFromResult: ({ data, isError, isLoading }) => ({
      data,
      isError,
      isLoading,
      credits: data?.account.credits ?? 0,
      hasActiveSubscription:
        (data?.account.plan?.isActive &&
          data?.account.plan?.interval !== 'one_time') ??
        false,
      isPending: selectIsPending(data),
    }),
  })

  const stripeCustomerId = userData?.stripeCustomerId
  const plan = useSelector(selectPlan)
  const isDefaultState = useSelector(selectItsDefaultState)

  const { hasHandleReports } = useRedirectBasedOnPermissions()

  useEffect(() => {
    dispatch(clearNewSearch())
    if (!isDefaultState) {
      hasHandleReports()
    }
  }, [isLoading])

  useEffect(() => {
    if (userData) {
      dispatch(setUserInfo(userData))
    }
  }, [userData, dispatch])

  return (
    <WeScreen
      customViewStyles="max-md:p-0"
      isError={isError}
      isLoading={isLoading}
      useHeader={false}
    >
      <div className="flex flex-col gap-5 items-center justify-center w-full h-full">
        {userData && (
          <UserCard
            plan={plan}
            profileImgUrl={userData.profilePicUrl}
            fullname={userData.firstname + ' ' + userData.lastname}
            credits={credits}
            impersonationCredits={userData.account.impersonationCredits ?? 0}
          />
        )}

        {!hasActiveSubscription && credits === 0 && (
          <PaymentCard
            status={plan?.status}
            stripeCustomerId={stripeCustomerId}
          />
        )}

        {credits === 0 && hasActiveSubscription && (
          <TopupCreditsCard stripeCustomerId={stripeCustomerId} />
        )}

        <ReportCard isProUser={hasActiveSubscription} />

        <ImpersonationCard />
      </div>
    </WeScreen>
  )
}
